import { useEffect, useState } from "react";
import { RefreshCw, Save, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Admin > Discord Health panel + community invite URL editor.
 * Extracted from Admin.jsx (Iter48) — pure presentational, callbacks come from props.
 */
export default function AdminDiscordHealth({ health, onLoad, onResyncTeam }) {
  const [inviteUrl, setInviteUrl] = useState("");
  const [savingInvite, setSavingInvite] = useState(false);
  const [threadsChannel, setThreadsChannel] = useState("");
  const [savingThreads, setSavingThreads] = useState(false);
  const [announceChannel, setAnnounceChannel] = useState("");
  const [savingAnnounce, setSavingAnnounce] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [purging, setPurging] = useState(false);
  const [purgeRound, setPurgeRound] = useState("");

  useEffect(() => {
    api.get("/community/discord-invite")
      .then((r) => setInviteUrl(r.data?.url || ""))
      .catch(() => {});
    api.get("/admin/community/match-threads-channel")
      .then((r) => setThreadsChannel(r.data?.channel_id || ""))
      .catch(() => {});
    api.get("/admin/community/announcements-channel")
      .then((r) => setAnnounceChannel(r.data?.channel_id || ""))
      .catch(() => {});
  }, []);

  const saveAnnounceChannel = async () => {
    setSavingAnnounce(true);
    try {
      await api.put("/admin/community/announcements-channel", { channel_id: announceChannel.trim() });
      toast.success(announceChannel.trim() ? "Announcements channel saved" : "Announcements channel cleared");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingAnnounce(false);
    }
  };

  const saveThreadsChannel = async () => {
    setSavingThreads(true);
    try {
      await api.put("/admin/community/match-threads-channel", { channel_id: threadsChannel.trim() });
      toast.success(threadsChannel.trim() ? "Match threads channel saved" : "Match threads channel cleared");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingThreads(false);
    }
  };

  const backfillThreads = async () => {
    if (!window.confirm("Create Discord match threads for every scheduled fixture that doesn't already have one? (batches of 8 · R1 first)")) return;
    setBackfilling(true);
    try {
      const { data } = await api.post("/admin/fixtures/backfill-threads");
      if (data.created === 0 && data.failed > 0) {
        toast.error(data.hint || `All ${data.failed} thread creates failed — check your channel ID + bot permissions.`,
          { duration: 12000 });
      } else {
        const remaining = data.remaining_without_thread || 0;
        const tail = remaining ? ` · ${remaining} more remaining — click again to continue` : "";
        toast.success(`Threads: ${data.created} created, ${data.failed} failed${tail}.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  const purgeThreads = async () => {
    const roundVal = purgeRound.trim();
    const roundInt = roundVal ? parseInt(roundVal, 10) : null;
    if (roundVal && (Number.isNaN(roundInt) || roundInt < 1)) {
      toast.error("Round must be a positive integer (or blank to purge every round).");
      return;
    }
    const scope = roundInt ? `EVERY R${roundInt} match thread` : "EVERY existing match thread";
    if (!window.confirm(`Delete ${scope} from Discord and reset tracking? This can't be undone.`)) return;
    setPurging(true);
    try {
      const url = "/admin/fixtures/purge-threads" + (roundInt ? `?round_number=${roundInt}` : "");
      const { data } = await api.post(url);
      const remaining = data.remaining_with_thread || 0;
      const tail = remaining ? ` · ${remaining} more remaining — click again to continue` : "";
      const scopeTxt = data.round_number ? ` (R${data.round_number})` : "";
      if (data.failed > 0) {
        toast.warning(`Purge${scopeTxt}: ${data.deleted} deleted, ${data.failed} failed${tail}. ${data.first_error || ""}`, { duration: 10000 });
      } else {
        toast.success(`Purge${scopeTxt}: ${data.deleted} deleted${tail}.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed");
    } finally {
      setPurging(false);
    }
  };

  const [syncingSlash, setSyncingSlash] = useState(false);
  const syncSlashCommands = async () => {
    if (!window.confirm("Register the /fixtures and /standings slash commands with Discord? Idempotent — safe to click multiple times.")) return;
    setSyncingSlash(true);
    try {
      const { data } = await api.post("/admin/discord/sync-slash-commands");
      toast.success(`Synced ${data.registered_count} slash command${data.registered_count === 1 ? "" : "s"} · ${data.commands.map((c) => "/" + c.name).join(", ")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed — verify DISCORD_PUBLIC_KEY, DISCORD_APPLICATION_ID (or DISCORD_CLIENT_ID) and DISCORD_BOT_TOKEN are set.");
    } finally {
      setSyncingSlash(false);
    }
  };

  const saveInvite = async () => {
    setSavingInvite(true);
    try {
      await api.put("/admin/community/discord-invite", { url: inviteUrl.trim() });
      toast.success(inviteUrl.trim() ? "Invite link saved" : "Invite link cleared");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingInvite(false);
    }
  };

  return (
    <section data-testid="admin-discord-health" className="space-y-5">
      {/* Community invite URL (Iter48) */}
      <div className="border border-[#5865F2]/40 bg-[#5865F2]/5 p-5 space-y-3" data-testid="admin-discord-invite-panel">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8b93f9] mb-1">/ COMMUNITY INVITE</div>
            <div className="font-heading font-black uppercase tracking-tight text-lg">Dashboard "Join our Discord" link</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-0.5">
              Shown to every logged-in member on the dashboard. Blank hides the card.
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 block">Invite URL</Label>
            <Input
              data-testid="admin-discord-invite-url"
              value={inviteUrl}
              onChange={(e) => setInviteUrl(e.target.value)}
              placeholder="https://discord.gg/your-invite-code"
              className="rounded-none bg-[#05050A] border-[#232330] font-mono"
            />
          </div>
          <Button
            onClick={saveInvite}
            disabled={savingInvite}
            data-testid="admin-discord-invite-save"
            className="rounded-none bg-[#5865F2] hover:bg-indigo-400 text-white uppercase tracking-widest text-xs font-bold"
          >
            <Save className="w-3.5 h-3.5 mr-2" /> {savingInvite ? "Saving…" : "Save invite"}
          </Button>
        </div>
      </div>

      {/* Iter85 · Slash commands sync — one-tap register of /fixtures + /standings with Discord. */}
      <div className="border border-[#F4D03F]/40 bg-[#F4D03F]/5 p-5 space-y-3" data-testid="admin-slash-commands-panel">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#F4D03F] mb-1">/ SLASH COMMANDS</div>
          <div className="font-heading font-black uppercase tracking-tight text-lg">Register Legacy slash commands with Discord</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-0.5 leading-5">
            Idempotent. Pushes <code className="text-[#F4D03F]">/fixtures</code> + <code className="text-[#F4D03F]">/standings</code> to the Discord API. Requires
            <code className="text-[#F4D03F]"> DISCORD_PUBLIC_KEY</code>, <code className="text-[#F4D03F]">DISCORD_APPLICATION_ID</code> (or <code className="text-[#F4D03F]">DISCORD_CLIENT_ID</code>), and <code className="text-[#F4D03F]">DISCORD_BOT_TOKEN</code>.
            After syncing, open the Discord Dev Portal and set the <strong>Interactions Endpoint URL</strong> to
            <code className="text-[#F4D03F] ml-1">https://legacyhll.com/api/discord/interactions</code>.
          </div>
        </div>
        <Button
          onClick={syncSlashCommands}
          disabled={syncingSlash}
          data-testid="admin-slash-commands-sync"
          className="rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black uppercase tracking-widest text-xs font-bold"
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-2 ${syncingSlash ? "animate-spin" : ""}`} />
          {syncingSlash ? "Syncing…" : "Sync slash commands"}
        </Button>
      </div>

      {/* Iter54 · Match threads parent channel + backfill (co-located with Discord config) */}
      <div className="border border-[#5865F2]/40 bg-[#5865F2]/5 p-5 space-y-3" data-testid="admin-match-threads-panel">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#8b93f9] mb-1">/ MATCH THREADS</div>
          <div className="font-heading font-black uppercase tracking-tight text-lg">Parent channel for auto-created match threads</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-0.5">
            Every fixture gets its own thread here. Reps see a "Match thread" button on their dashboard that opens it and pings both reps.
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 items-end">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 block">Discord channel ID (numeric)</Label>
            <Input
              data-testid="admin-match-threads-channel"
              value={threadsChannel}
              onChange={(e) => setThreadsChannel(e.target.value)}
              placeholder="e.g. 1387463492059136225"
              className="rounded-none bg-[#05050A] border-[#232330] font-mono"
            />
          </div>
          <Button
            onClick={saveThreadsChannel}
            disabled={savingThreads}
            data-testid="admin-match-threads-save"
            className="rounded-none bg-[#5865F2] hover:bg-indigo-400 text-white uppercase tracking-widest text-xs font-bold"
          >
            <Save className="w-3.5 h-3.5 mr-2" /> {savingThreads ? "Saving…" : "Save channel"}
          </Button>
          <Button
            onClick={backfillThreads}
            disabled={backfilling || !threadsChannel}
            data-testid="admin-match-threads-backfill"
            className="rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black uppercase tracking-widest text-xs font-bold disabled:opacity-40"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-2" /> {backfilling ? "Working…" : "Backfill threads"}
          </Button>
          <Input
            value={purgeRound}
            onChange={(e) => setPurgeRound(e.target.value)}
            placeholder="R# (blank=all)"
            data-testid="admin-match-threads-purge-round"
            className="rounded-none bg-black border-red-500/40 text-red-400 uppercase tracking-widest text-xs w-32 focus:border-red-500"
            title="Optional — enter a round number to purge only that round (e.g., 7). Leave blank to purge every round."
          />
          <Button
            onClick={purgeThreads}
            disabled={purging || !threadsChannel}
            data-testid="admin-match-threads-purge"
            variant="outline"
            className="rounded-none border-red-500 text-red-500 hover:bg-red-500 hover:text-white uppercase tracking-widest text-xs font-bold disabled:opacity-40"
            title="Delete existing match threads and clear tracking. Set the round input first to scope to one round; leave blank to wipe everything."
          >
            <Trash2 className="w-3.5 h-3.5 mr-2" /> {purging ? "Purging…" : (purgeRound.trim() ? `Purge R${purgeRound.trim()}` : "Purge all")}
          </Button>
        </div>
      </div>

      {/* Iter63 · Announcements channel for auto season slate broadcasts */}
      <div className="border border-orange-500/40 bg-orange-500/5 p-5 space-y-3" data-testid="admin-announcements-panel">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-orange-400 mb-1">/ ANNOUNCEMENTS</div>
          <div className="font-heading font-black uppercase tracking-tight text-lg">Channel for auto season slate broadcasts</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-0.5">
            Every time you draw a season or bulk-reschedule a round, an updated slate embed is posted here. Leave blank to disable auto-broadcasts.
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 block">Discord channel ID (numeric)</Label>
            <Input
              data-testid="admin-announcements-channel"
              value={announceChannel}
              onChange={(e) => setAnnounceChannel(e.target.value)}
              placeholder="e.g. 1387463492059136225"
              className="rounded-none bg-[#05050A] border-[#232330] font-mono"
            />
          </div>
          <Button
            onClick={saveAnnounceChannel}
            disabled={savingAnnounce}
            data-testid="admin-announcements-save"
            className="rounded-none bg-orange-500 hover:bg-orange-400 text-black uppercase tracking-widest text-xs font-bold"
          >
            <Save className="w-3.5 h-3.5 mr-2" /> {savingAnnounce ? "Saving…" : "Save channel"}
          </Button>
        </div>
      </div>

      {/* Discord Health */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-1">
        <div>
          <div className="font-heading text-xl font-black uppercase tracking-tight">Discord health</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
            Per-team sync status · missing roles or channels surface here before they bite.
          </div>
        </div>
        <Button onClick={onLoad} data-testid="admin-discord-health-refresh"
                className="rounded-none bg-[#5865F2] hover:bg-indigo-400 text-white uppercase tracking-widest text-xs font-bold">
          <RefreshCw className="w-3.5 h-3.5 mr-2" /> {health ? "Refresh" : "Load health"}
        </Button>
      </div>

      {health ? (
        <>
          <div className="flex flex-wrap gap-3 text-[10px] font-mono uppercase tracking-widest">
            <span className={`px-3 py-1.5 border ${health.enabled ? "border-emerald-400 text-emerald-400" : "border-red-500 text-red-500"}`}>
              Bot · {health.enabled ? "ONLINE" : "OFFLINE"}
            </span>
            <span data-testid="admin-discord-health-ok" className="px-3 py-1.5 border border-emerald-400 text-emerald-400">✓ {health.ok} synced</span>
            <span data-testid="admin-discord-health-missing" className="px-3 py-1.5 border border-[#F4D03F] text-[#F4D03F]">⚠ {health.missing} missing</span>
            <span data-testid="admin-discord-health-errored" className="px-3 py-1.5 border border-red-500 text-red-500">✗ {health.errored} errored</span>
            <span className="px-3 py-1.5 border border-[#232330] text-gray-400">{health.total} teams</span>
          </div>
          <div className="border border-[#232330] bg-[#101015] divide-y divide-[#232330]">
            {health.rows.map((r) => {
              const tone = r.state === "ok"
                ? "border-l-emerald-400"
                : r.state === "errored" ? "border-l-red-500" : "border-l-[#F4D03F]";
              return (
                <div key={r.team_id} data-testid={`health-row-${r.tag}`} className={`p-3 flex flex-wrap items-center gap-3 border-l-4 ${tone}`} style={{ borderLeftColor: r.logo_color || undefined }}>
                  <div className="flex-1 min-w-[180px]">
                    <div className="font-heading font-black uppercase tracking-tight" style={{ color: r.logo_color }}>{r.tag}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">{r.name} · {r.tier || "Mains"}</div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 text-[10px] font-mono uppercase tracking-widest">
                    <span className={`px-2 py-1 border ${r.role_id ? "border-emerald-400 text-emerald-400" : "border-[#F4D03F] text-[#F4D03F]"}`}>role {r.role_id ? "✓" : "—"}</span>
                    <span className={`px-2 py-1 border ${r.rep_role_id ? "border-emerald-400 text-emerald-400" : "border-[#F4D03F] text-[#F4D03F]"}`}>rep {r.rep_role_id ? "✓" : "—"}</span>
                    <span className={`px-2 py-1 border ${r.channel_id ? "border-emerald-400 text-emerald-400" : "border-[#F4D03F] text-[#F4D03F]"}`}>chan {r.channel_id ? "✓" : "—"}</span>
                    {r.synced_at && <span className="px-2 py-1 border border-[#232330] text-gray-500">{new Date(r.synced_at).toLocaleDateString()}</span>}
                  </div>
                  {r.last_errors?.length ? (
                    <div className="w-full text-[10px] font-mono text-red-400 mt-1 pl-1">
                      {r.last_errors.map((e, i) => <div key={i}>• {e}</div>)}
                    </div>
                  ) : null}
                  <button
                    onClick={() => onResyncTeam({ id: r.team_id, tag: r.tag, discord_channel_id: r.channel_id })}
                    data-testid={`health-resync-${r.tag}`}
                    className="text-[10px] font-mono uppercase tracking-widest text-[#5865F2] border border-[#5865F2] hover:bg-[#5865F2] hover:text-white px-3 py-1.5 font-bold"
                  >
                    {r.channel_id ? "Re-sync" : "Sync"}
                  </button>
                </div>
              );
            })}
            {health.rows.length === 0 && (
              <div className="px-4 py-6 text-center text-gray-500 font-mono text-xs uppercase tracking-widest">
                No approved teams yet.
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="px-4 py-10 text-center border border-dashed border-[#232330] text-gray-500 font-mono text-xs uppercase tracking-widest">
          Hit "Load health" to fetch the current Discord sync snapshot.
        </div>
      )}
    </section>
  );
}
